import { useState, useEffect } from 'react';
import { getNicknames, reserveNicknames, setNickname } from '../services/api';
import { useMetadata } from '../context/MetadataContext';
import { useUser } from '../context/UserContext';
import { useGlobalError } from '../context/GlobalErrorContext';

/**
 * Nickname list and nickname change for NicknameModal.
 *
 * Loads the available nicknames whenever `isOpen` becomes true.
 *
 * @param {boolean} isOpen - whether the modal is open
 * @returns {{ availableNicknames, isLoading, isChanging, getMoreNicknames, changeNickname }}
 */
export const useNicknames = (isOpen) => {
  const { getText } = useMetadata();
  const { updateUserData } = useUser();
  const { triggerError } = useGlobalError();
  const [availableNicknames, setAvailableNicknames] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isChanging, setIsChanging] = useState(false);

  // Load nicknames when modal opens
  useEffect(() => {
    if (isOpen) {
      loadNicknames();
    }
  }, [isOpen]); 

  const loadNicknames = async () => {
    setIsLoading(true);
    try {
      const response = await getNicknames();
      setAvailableNicknames(response.nicknames || []);
    } catch (error) {
      console.error('Error loading nicknames:', error);
      triggerError('high', getText('errors.request_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const getMoreNicknames = async () => {
    setIsLoading(true);
    try {
      const response = await reserveNicknames();
      setAvailableNicknames(response.nicknames || []);
    } catch (error) {
      console.error('Error reserving nicknames:', error);
      triggerError('high', getText('errors.request_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * @param {string} nickname - the nickname to switch to
   * @param {Function} onChanged - called after a successful change
   */
  const changeNickname = async (nickname, onChanged) => {
    if (!nickname) return;
    
    setIsChanging(true);
    try {
      await setNickname(nickname);
      // Update user context with new nickname
      updateUserData({ nickname });
      onChanged();
    } catch (error) {
      console.error('Error changing nickname:', error);
      triggerError('high', getText('errors.request_failed'))
    } finally {
      setIsChanging(false);
    }
  };

  return {
    availableNicknames,
    isLoading,
    isChanging,
    getMoreNicknames,
    changeNickname,
  };
};

export default useNicknames;
